import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { EN } from './i18n.en';

/**
 * طبقةُ التعريبِ/الأنجلزة.
 *
 * النظامُ عربيٌّ في الأصل، فالترجمةُ تُبنى على «المصدرِ العربيّ مفتاحًا»: يُلفُّ
 * النصُّ بـ`t('النصّ العربيّ')`، فإن كانت اللغةُ إنجليزيّةً أُرجعت ترجمتُه من
 * القاموس، وإلّا رجع النصُّ كما هو. هذا يسمح بالأنجلزةِ التدريجيّةِ دون مفاتيحَ
 * مخترَعةٍ ودون كسرِ ما لم يُترجَم بعد.
 */
export type Locale = 'ar' | 'en';
const KEY = 'goyone.locale';

export function readLocale(): Locale {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'en' || v === 'ar') return v;
  } catch {
    /* تجاهل */
  }
  return 'ar';
}

function translate(locale: Locale, ar: string): string {
  if (locale !== 'en') return ar;
  return EN[ar] ?? ar;
}

interface I18nValue {
  locale: Locale;
  dir: 'rtl' | 'ltr';
  setLocale: (l: Locale) => void;
  t: (ar: string) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readLocale);
  const dir: 'rtl' | 'ltr' = locale === 'ar' ? 'rtl' : 'ltr';

  /* اتجاهُ الصفحةِ ولغتُها على الجذرِ — فيتبدّل التخطيطُ كلُّه، وتُحفظ التفضيلة. */
  useEffect(() => {
    const el = document.documentElement;
    el.lang = locale;
    el.dir = dir;
    try {
      localStorage.setItem(KEY, locale);
    } catch {
      /* تجاهل */
    }
  }, [locale, dir]);

  const value: I18nValue = {
    locale,
    dir,
    setLocale: setLocaleState,
    t: (ar: string) => translate(locale, ar),
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const v = useContext(I18nContext);
  if (!v) throw new Error('useI18n يجب أن يُستخدم داخل I18nProvider.');
  return v;
}

/**
 * ترجمةٌ خارجَ شجرةِ المكوّنات (مثلَ جداولِ التنقّلِ الثابتة) — تقرأُ اللغةَ
 * الحاليّةَ من جذرِ المستند. تُستعمَل حيث لا يتوفّر hook.
 */
export function tt(ar: string): string {
  const en = typeof document !== 'undefined' && document.documentElement.lang === 'en';
  return en ? EN[ar] ?? ar : ar;
}
